import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import StartClassModal from "./StartClassModal";
import { BASE_URLS, getClassApiUrl } from "../config/globalConfig";

export default function Header() {
    const navigate = useNavigate();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [userRole, setUserRole] = useState("");
    const [userId, setUserId] = useState("");
    const [showStartClassModal, setShowStartClassModal] = useState(false);
    const [hasActiveClass, setHasActiveClass] = useState(false);
    const [activeClass, setActiveClass] = useState(null);
    const [pathname, setPathname] = useState(window.location.pathname);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [expandedMobileCategory, setExpandedMobileCategory] = useState(null);

    const dropdownRef = useRef(null);

    // Course categories and their courses
    const courseCategories = {
        'Voice': [
            { name: 'Opera Singing', path: '/opera-singing' },
            { name: 'Hindustani Classical Singing', path: '/hindustani-singing' },
            { name: 'Western Contemporary Singing', path: '/western-singing' },
            { name: 'Bollywood Film Singing', path: '/bollywood-singing' }
        ],
        'Instruments': [
            { name: 'Guitar(Acoustic & Electric)', path: '/guitar' },
            { name: 'Piano/Keyboard', path: '/piano' },
            { name: 'Violin', path: '/violin' }
        ],
        'Music Production': [
            { name: 'Music Production', path: '/music-production' }
        ],
        'All Access': [
            { name: 'Dual Program', path: '/dual-program' },
            { name: 'All-in-One Program', path: '/all-in-one' }
        ]
    };

    const toggleDropdown = (e) => {
        e.preventDefault();
        setIsDropdownOpen(!isDropdownOpen);
    };

    const toggleMobileMenu = () => {
        const newState = !isMobileMenuOpen;
        setIsMobileMenuOpen(newState);
        setIsDropdownOpen(false); // Close dropdown when mobile menu opens
        
        // Add/remove body class for mobile menu state
        if (newState) {
            document.body.classList.add('mobile-menu-open');
        } else {
            document.body.classList.remove('mobile-menu-open');
        }
    };

    const closeMobileMenu = () => {
        setIsMobileMenuOpen(false);
        setIsDropdownOpen(false);
        setExpandedMobileCategory(null);
        
        // Remove body class when mobile menu closes
        document.body.classList.remove('mobile-menu-open');
    };

    const handleMobileCategoryClick = (category) => {
        // Toggle accordion: if same category clicked, close it; otherwise open new one
        setExpandedMobileCategory(expandedMobileCategory === category ? null : category);
    };

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };
        if (isDropdownOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isDropdownOpen]);

    // Close dropdown on route change (React Router or manual navigation)
    useEffect(() => {
        const handleLocationChange = () => {
            setPathname(window.location.pathname);
            setIsDropdownOpen(false);
            setIsMobileMenuOpen(false);
            document.body.classList.remove('mobile-menu-open');
        };
        window.addEventListener("popstate", handleLocationChange);
        return () => {
            window.removeEventListener("popstate", handleLocationChange);
        };
    }, []);

    // Close mobile menu on route change
    useEffect(() => {
        const handleRouteChange = () => {
            setIsMobileMenuOpen(false);
            setIsDropdownOpen(false);
            document.body.classList.remove('mobile-menu-open');
        };
        return () => {
            window.removeEventListener("popstate", handleRouteChange);
        };
    }, []);

    // Sticky header on scroll
    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 100);
        };
        window.addEventListener("scroll", handleScroll);
        handleScroll(); // run once
        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    // Check authentication
    useEffect(() => {
        const checkAuthStatus = () => {
            try {
                // Function to get cookie value by name
                const getCookie = (name) => {
                    const value = `; ${document.cookie}`;
                    const parts = value.split(`; ${name}=`);
                    if (parts.length === 2) return parts.pop().split(';').shift();
                    return null;
                };

                // Check if user authentication cookies exist
                const userId = getCookie('User_Id');
                const userRoleId = getCookie('User_Role_Id');
                const userEmail = getCookie('User_Email');
                const userRole = getCookie('User_Role');

                // If any of the required authentication cookies are missing, user is not logged in
                if (!userId || !userRoleId || !userEmail || !userRole) {
                    setIsAuthenticated(false);
                    setUserRole("");
                    setUserId("");
                } else {
                    // If all required cookies exist, user is considered logged in
                    setIsAuthenticated(true);
                    setUserRole(userRole);
                    setUserId(userId);
                }
            } catch (error) {
                console.error("Error checking authentication:", error);
                setIsAuthenticated(false);
                setUserRole("");
                setUserId("");
            } finally {
                setIsLoading(false);
            }
        };
        checkAuthStatus();
    }, []);

    // Check active class for student
    useEffect(() => {
        if (isAuthenticated && userRole.toLowerCase() === "student" && userId) {
            console.log('Checking active class for student:', userId);
            const apiUrl = `${getClassApiUrl('GET_STUDENT_ACTIVE_CLASS')}?student_id=${userId}`;
            console.log('Active class API URL:', apiUrl);

            axios
                .get(apiUrl)
                .then((response) => {
                    console.log('Active class API response:', response.data);
                    if (response.data.result) {
                        setHasActiveClass(response.data.hasActiveClass);
                        setActiveClass(response.data.Class);
                        console.log('Active class set:', response.data.Class);
                    } else {
                        console.log('No active class found for student');
                        setHasActiveClass(false);
                        setActiveClass(null);
                    }
                })
                .catch((error) => {
                    console.error("Error checking active class:", error);
                    setHasActiveClass(false);
                    setActiveClass(null);
                });
        }
    }, [isAuthenticated, userRole, userId]);

    const handleStartClass = (classId) => {
        navigate(`/live-class/${classId}`);
    };

    const handleJoinClass = () => {
        if (activeClass) {
            navigate(`/live-class/${activeClass.id}`);
        } else {
            // Show a message to the user that no active class is available
            alert('No active class found. Please contact your teacher or check your class schedule.');
        }
    };

    const isDropdownActive = [
        "/guitar",
        "/piano",
        "/opera-singing",
        "/western-singing",
        "/bollywood-singing",
        "/hindustani-singing",
        "/violin",
        "/music-production",
        "/dual-program",
        "/all-in-one",
    ].includes(pathname);

    return (
        <>
            <header className={`fixed-header ${isScrolled ? 'active-sticky' : ''}`}>
                <div className="header-container top-bar-header-container">
                    <div className="menusbar contactBar">
                        <li>
                            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                            </svg>
                            <a href="tel:+91-8769373591">+91-8769373591</a>
                        </li>
                        <li>
                            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                                <polyline points="22,6 12,13 2,6"/>
                            </svg>
                            <a href="mailto:harshitaschoolofmusic@gmail.com">harshitaschoolofmusic@gmail.com</a>
                        </li>
                    </div>
                    <div className="menusbar socialBar">
                        <li>
                            <a href="#">
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                                </svg>
                            </a>
                        </li>
                        <li>
                            <a href="https://www.instagram.com/harshitaschoolofmusic/">
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                                </svg>
                            </a>
                        </li>
                        <li>
                            <a href="#">
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                                </svg>
                            </a>
                        </li>
                    </div>
                </div>
                <div className="header-container bottom-bar-header-container">
                    <div className="header-logo">
                        <Link to="/" onClick={closeMobileMenu}>
                            <img src="/images/hsm_logo.png" alt="HSM Harshita School Of Music" />
                        </Link>
                    </div>

                    {/* Mobile Menu Button */}
                    <button 
                        className={`mobile-menu-toggle ${isMobileMenuOpen ? 'active' : ''}`}
                        onClick={toggleMobileMenu}
                        aria-label="Toggle mobile menu"
                    >
                        <span></span>
                        <span></span>
                        <span></span>
                    </button>

                    <nav className={`header-nav ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
                        <ul className="nav-menu">
                            <li>
                                <Link
                                    to="/"
                                    className={`nav-link ${pathname === '/' ? 'active' : ''}`}
                                    onClick={closeMobileMenu}
                                >
                                    HOME
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/about-us"
                                    className={`nav-link ${pathname === '/about-us' ? 'active' : ''}`}
                                    onClick={closeMobileMenu}
                                >
                                    ABOUT US
                                </Link>
                            </li>
                            <li className="dropdown" ref={dropdownRef}>
                                <a
                                    href="#"
                                    className={`nav-link dropdown-toggle ${isDropdownActive ? 'active' : ''}`}
                                    onClick={toggleDropdown}
                                >
                                    COURSES <span className={`dropdown-arrow ${isDropdownOpen ? 'rotated' : ''}`}>▼</span>
                                </a>
                                <div className={`dropdown-menu ${isDropdownOpen ? 'show' : ''}`}>
                                    {/* Show accordion on mobile, or all courses on desktop */}
                                    {window.innerWidth <= 1300 ? (
                                        // Mobile view: Accordion-style categories
                                        <div className="mobile-accordion-view">
                                            {Object.keys(courseCategories).map((category, index) => (
                                                <div key={index} className="mobile-accordion-item">
                                                    <button
                                                        className={`mobile-accordion-header ${expandedMobileCategory === category ? 'expanded' : ''}`}
                                                        onClick={() => handleMobileCategoryClick(category)}
                                                    >
                                                        <span className="accordion-title">{category}</span>
                                                        <span className={`accordion-arrow ${expandedMobileCategory === category ? 'expanded' : ''}`}>
                                                            ▼
                                                        </span>
                                                    </button>
                                                    <div className={`mobile-accordion-content ${expandedMobileCategory === category ? 'expanded' : ''}`}>
                                                        <div className="accordion-courses-list">
                                                            {courseCategories[category].map((course, courseIndex) => (
                                                                <Link
                                                                    key={courseIndex}
                                                                    to={course.path}
                                                                    className={`accordion-course-item ${pathname === course.path ? 'active' : ''}`}
                                                                    onClick={closeMobileMenu}
                                                                >
                                                                    {course.name}
                                                                </Link>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        // Desktop view: Show all courses in columns
                                        <>
                                            <div className="column-dropdown-header">
                                                <span>Voice</span>
                                                <ul>
                                                    <li>
                                                        <Link
                                                            to="/opera-singing"
                                                            className={`dropdown-item ${pathname === '/opera-singing' ? 'active' : ''}`}
                                                            onClick={closeMobileMenu}
                                                        >
                                                            Opera Singing
                                                        </Link>
                                                    </li>
                                                    <li>
                                                        <Link
                                                            to="/hindustani-singing"
                                                            className={`dropdown-item ${pathname === '/hindustani-singing' ? 'active' : ''}`}
                                                            onClick={closeMobileMenu}
                                                        >
                                                            Hindustani Classical Singing
                                                        </Link>
                                                    </li>
                                                    <li>
                                                        <Link
                                                            to="/western-singing"
                                                            className={`dropdown-item ${pathname === '/western-singing' ? 'active' : ''}`}
                                                            onClick={closeMobileMenu}
                                                        >
                                                            Western Contemporary Singing
                                                        </Link>
                                                    </li>
                                                    <li>
                                                        <Link
                                                            to="/bollywood-singing"
                                                            className={`dropdown-item ${pathname === '/bollywood-singing' ? 'active' : ''}`}
                                                            onClick={closeMobileMenu}
                                                        >
                                                            Bollywood Film Singing
                                                        </Link>
                                                    </li>
                                                </ul>
                                            </div>
                                            <div className="column-dropdown-header">
                                                <span>Instruments</span>
                                                <ul>
                                                    <li>
                                                        <Link
                                                            to="/guitar"
                                                            className={`dropdown-item ${pathname === '/guitar' ? 'active' : ''}`}
                                                            onClick={closeMobileMenu}
                                                        >
                                                            Guitar(Acoustic & Electric)
                                                        </Link>
                                                    </li>
                                                    <li>
                                                        <Link
                                                            to="/piano"
                                                            className={`dropdown-item ${pathname === '/piano' ? 'active' : ''}`}
                                                            onClick={closeMobileMenu}
                                                        >
                                                            Piano/Keyboard
                                                        </Link>
                                                    </li>
                                                    <li>
                                                        <Link
                                                            to="/violin"
                                                            className={`dropdown-item ${pathname === '/violin' ? 'active' : ''}`}
                                                            onClick={closeMobileMenu}
                                                        >
                                                            Violin
                                                        </Link>
                                                    </li>
                                                </ul>
                                            </div>
                                            <div className="column-dropdown-header">
                                                <span>Music Production</span>
                                                <ul>
                                                    <li>
                                                        <Link
                                                            to="/music-production"
                                                            className={`dropdown-item ${pathname === '/music-production' ? 'active' : ''}`}
                                                            onClick={closeMobileMenu}
                                                        >
                                                            Music Production
                                                        </Link>
                                                    </li>
                                                </ul>
                                            </div>
                                            <div className="column-dropdown-header">
                                                <span>All Access</span>
                                                <ul>
                                                    <li>
                                                        <Link
                                                            to="/dual-program"
                                                            className={`dropdown-item ${pathname === '/dual-program' ? 'active' : ''}`}
                                                            onClick={closeMobileMenu}
                                                        >
                                                            Dual Program
                                                        </Link>
                                                    </li>
                                                    <li>
                                                        <Link
                                                            to="/all-in-one"
                                                            className={`dropdown-item ${pathname === '/all-in-one' ? 'active' : ''}`}
                                                            onClick={closeMobileMenu}
                                                        >
                                                            All-in-One Program
                                                        </Link>
                                                    </li>
                                                </ul>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </li>
                            <li>
                                <Link
                                    to="/hsm-offline"
                                    className={`nav-link ${pathname === '/hsm-offline' ? 'active' : ''}`}
                                    onClick={closeMobileMenu}
                                >
                                    HSM OFFLINE
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/admission-form"
                                    className={`nav-link ${pathname === '/admission-form' ? 'active' : ''}`}
                                    onClick={closeMobileMenu}
                                >
                                    ADMISSION FORM
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/faq"
                                    className={`nav-link ${pathname === '/faq' ? 'active' : ''}`}
                                    onClick={closeMobileMenu}
                                >
                                    FAQ'S
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/contact"
                                    className={`nav-link ${pathname === '/contact' ? 'active' : ''}`}
                                    onClick={closeMobileMenu}
                                >
                                    CONTACT
                                </Link>
                            </li>
                            <div className="header-cta mobile-btn">
                                {isLoading ? (
                                    <div className="login-btn">
                                        Loading...
                                    </div>
                                ) : isAuthenticated ? (
                                    <div className="flex gap-2">
                                        <a href={BASE_URLS.ADMIN_DASHBOARD_URL} className="login-btn">
                                            {/* Dashboard */}
                                            Login to Dashboard
                                        </a>
                                    </div>
                                ) : (
                                    <a href={BASE_URLS.ADMIN_LOGIN_URL} className="login-btn">
                                        {/* Login */}
                                        Login to Dashboard
                                    </a>
                                )}
                            </div>
                        </ul>
                    </nav>
                </div>

                {/* Mobile Menu Overlay */}
                {isMobileMenuOpen && (
                    <div className="mobile-menu-overlay" onClick={closeMobileMenu}></div>
                )}

                {/* Start Class Modal */}
                <StartClassModal
                    isOpen={showStartClassModal}
                    onClose={() => setShowStartClassModal(false)}
                    onStartClass={handleStartClass}
                    teacherId={userId}
                />
            </header>
        </>
    );
}